// Query-Keys und Echtzeit-Abgleich für Kanal-Exporte (Socket-Ereignis EXPORT_UPDATE, docs/API.md 3.12).
import type { ExportFormat, ExportJobDto, JobStatus } from '@lagerfeuer/shared';
import { compareSnowflakes } from '@lagerfeuer/shared';
import type { QueryClient } from '@tanstack/react-query';
import { formatBytes } from '../format';
import { toast } from '../toast';
import { sameOriginUrl } from '../safe-url';

/** Präfix aller Export-Queries. */
export const EXPORTS_KEY = ['extras', 'exports'] as const;
export const EXPORTS_LIST_KEY = [...EXPORTS_KEY, 'list'] as const;
export const exportJobKey = (id: string) => [...EXPORTS_KEY, 'job', id] as const;

/** Polling-Intervall laufender Exporte. */
export const EXPORT_POLL_MS = 1500;

export const isExportActive = (status: JobStatus): boolean => status === 'PENDING' || status === 'RUNNING';

export const FORMAT_LABEL: Record<ExportFormat, string> = { JSON: 'JSON', HTML: 'HTML', MARKDOWN: 'Markdown' };

export function sortExports(items: ExportJobDto[]): ExportJobDto[] {
  return [...items].sort((a, b) => compareSnowflakes(b.id, a.id));
}

/** Download per verstecktem <a download> auslösen (signierte URL, kein Auth-Header nötig). */
export function triggerDownload(url: string): void {
  const safe = sameOriginUrl(url);
  if (!safe) return;
  const a = document.createElement('a');
  a.href = safe;
  a.download = '';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Update in Einzel- und Listen-Cache übernehmen; beim Wechsel auf DONE Toast „Export fertig". */
export function applyExportUpdate(queryClient: QueryClient, dto: ExportJobDto): void {
  const prev = queryClient.getQueryData<ExportJobDto>(exportJobKey(dto.id)) ?? queryClient.getQueryData<ExportJobDto[]>(EXPORTS_LIST_KEY)?.find((e) => e.id === dto.id);
  queryClient.setQueryData<ExportJobDto>(exportJobKey(dto.id), dto);
  queryClient.setQueryData<ExportJobDto[]>(EXPORTS_LIST_KEY, (list) => (list ? sortExports([...list.filter((e) => e.id !== dto.id), dto]) : list));
  if (dto.status === 'DONE' && prev?.status !== 'DONE') {
    const url = dto.downloadUrl;
    toast({
      kind: 'success',
      id: `export-${dto.id}`,
      title: 'Export fertig',
      body: `${FORMAT_LABEL[dto.format]}-Export${dto.sizeBytes !== null ? ` · ${formatBytes(dto.sizeBytes)}` : ''}`,
      href: '/exports',
      action: url ? { label: 'Herunterladen', run: () => triggerDownload(url) } : undefined,
      timeout: 15_000,
    });
  }
}
